/**
 * The price-curves look, shared by the deck slide and the card (plots plan G).
 *
 * One ramp, one dash per role: the deck's `PriceSensitivitySlide` treatment is
 * the reference, and the card and the survey tab draw the same four strokes so
 * a reader who has seen the slide recognises the card. The falling pair reads
 * light to mid, the rising pair mid to dark, widths grow with severity.
 *
 * Tokens are deck-ui `--slide-*` tokens (defined on `:root` in `tokens.css`);
 * the fallbacks are the same values, for a host page that has not loaded them.
 */

import { PlotDataOf } from '../contract'

export type PriceCurvesRole = PlotDataOf<'price-curves'>['series'][number]['role']

export interface PriceCurvesSeriesStyle {
  stroke: string
  width: number
  dash?: string
}

export const PRICE_SERIES_STYLE: Record<PriceCurvesRole, PriceCurvesSeriesStyle> = {
  too_cheap: { stroke: 'var(--slide-chart-7, hsl(156 72% 67%))', width: 2, dash: '2 5' },
  cheap: { stroke: 'var(--slide-chart-5, hsl(160 84% 39%))', width: 2.2, dash: '11 5' },
  expensive: { stroke: 'var(--slide-chart-1, hsl(161 94% 30%))', width: 2.2, dash: '13 4 3 4' },
  too_expensive: { stroke: 'var(--slide-chart-2, hsl(164 86% 16%))', width: 2.8 },
}

/** Low to high, the order the recipe fields the four thresholds in. */
export const PRICE_SERIES_ORDER: ReadonlyArray<PriceCurvesRole> = [
  'too_cheap',
  'cheap',
  'expensive',
  'too_expensive',
]

/** The four share gridlines every surface draws (0 to 100% of the base). */
export const PRICE_SHARE_TICKS: ReadonlyArray<number> = [0, 25, 50, 75, 100]

/** Candidate tick steps within a decade, ascending. */
const NICE_STEPS = [1, 2, 2.5, 5, 10]

/**
 * Round price ticks across a window, between `minTicks` and `maxTicks` of them.
 *
 * The smallest nice step that yields at most `maxTicks` ticks inside the
 * window; when even that leaves fewer than `minTicks`, the window's own two
 * ends are the ticks. Display only: the ticks never move a mark.
 */
export function priceTicks(
  window: { low: number; high: number },
  minTicks = 3,
  maxTicks = 5,
): Array<number> {
  const span = window.high - window.low
  if (!Number.isFinite(span) || span <= 0) {
    return []
  }
  const baseDecade = Math.pow(10, Math.floor(Math.log10(span / maxTicks)))
  for (const decade of [baseDecade / 10, baseDecade, baseDecade * 10, baseDecade * 100]) {
    for (const candidate of NICE_STEPS) {
      const step = candidate * decade
      const ticks = ticksFor(window, step)
      if (ticks.length <= maxTicks && ticks.length >= minTicks) {
        return ticks
      }
      if (ticks.length < minTicks) {
        return [window.low, window.high]
      }
    }
  }
  return [window.low, window.high]
}

function ticksFor(window: { low: number; high: number }, step: number): Array<number> {
  const ticks: Array<number> = []
  const first = Math.ceil(window.low / step - 1e-9) * step
  for (let value = first; value <= window.high + step * 1e-9; value += step) {
    ticks.push(Number(value.toFixed(6)))
  }
  return ticks
}

/**
 * A price, as text. Unlabelled when the payload carries no currency code: a
 * "32" that silently means dollars to the reader and pounds to the respondent
 * is worse than a 32 with no symbol. The decimals are Python's (`decimals`),
 * never re-derived here.
 */
export function formatPlotPrice(
  value: number,
  currencyCode: string | null | undefined,
  decimals: number,
  locale?: string,
): string {
  if (!Number.isFinite(value)) {
    return ''
  }
  const digits = { maximumFractionDigits: decimals, minimumFractionDigits: decimals }
  if (currencyCode) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode, ...digits }).format(value)
    } catch {
      // An unknown code prints the number alone rather than a wrong symbol.
    }
  }
  return new Intl.NumberFormat(locale, digits).format(value)
}

/** Fills `{name}` placeholders of a `deck_labels` template; unknown names stay. */
export function fillTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  )
}
